<template>
    <app-layout>
        <div class="flex flex-col">
            <page-header title="Incident Log">
                <div class="mt-3 flex items-start justify-between">
                    <filter-bar v-model="queryParams" :config="filterBarConfig" />
                </div>
            </page-header>
            <data-table :columns="fields" :items="incidents" :no-items-view="{ noItems: 'No data available' }" :query-params="queryParams">
                <template #start_date="{ item }">
                    <td class="align-middle">
                        {{ dateTimeMedium(item.start_date, currentLocale) }}
                    </td>
                </template>

                <template #end_date="{ item }">
                    <td class="align-middle">
                        <div v-if="item.end_date">
                            {{ dateTimeMedium(item.end_date, currentLocale) }}
                        </div>
                        <div v-else class="text-danger">OPEN</div>
                    </td>
                </template>

                <template #duration="{ item }">
                    <td class="align-middle">
                        {{ formatSecondsToDDDHHMM(item.duration) }}
                    </td>
                </template>

                <template #type="{ item }">
                    <td :title="item.type.label" class="align-middle">
                        {{ item.type.shortLabel }}
                    </td>
                </template>

                <template #report="{ item }">
                    <td class="align-middle">
                        <div>{{ item.report || '-' }}</div>
                    </td>
                </template>

                <template #reporter="{ item }">
                    <td class="align-middle">
                        <div>{{ item.reporter.name }}</div>
                    </td>
                </template>

                <template #curie_id="{ item }">
                    <td class="align-middle">
                        <a :href="route('assets.show', item.asset_id)" @click.prevent="openAssetDetails(item.asset_id)">{{ item.curie_id }}</a>
                    </td>
                </template>

                <template #tickets="{ item }">
                    <td class="align-middle">
                        <ul v-if="item.tickets.length" class="list-unstyled m-0">
                            <li v-for="ticket in item.tickets" :key="ticket">
                                <a :href="route('workorders.show', ticket.id)" class="truncate" @click.prevent="showWorkorder(ticket.id)">{{
                                    ticket.number
                                }}</a>
                            </li>
                        </ul>
                        <span v-else>-</span>
                    </td>
                </template>
            </data-table>
        </div>
    </app-layout>
</template>
<script setup>
import dateTimeMedium from '@/filters/dateTimeMedium.js';
import AppLayout from '@/Layouts/AppLayout.vue';
import FilterBar from '@/Components/FilterBar.vue';
import PageHeader from '@/Components/PageHeader.vue';
import { computed, ref } from 'vue';
import { trans } from '@/mixins/i18n.js';
import DataTable from '@/Components/DataTable.vue';
import { router } from '@inertiajs/vue3';
import { visitModal } from '@inertiaui/modal-vue';
import { formatSecondsToDDDHHMM } from '@/Utils/formatDuration.ts';

const props = defineProps({
    incidents: Object,
    filters: Object,
    specificLocations: Array,
});

const queryParams = ref(null);

const specificLocations = computed(() => (props.filters.specificLocations?.data || []).map((loc) => ({ label: loc.name, value: loc.name })));
const areas = computed(() => (props.filters.areas?.data || []).map((loc) => ({ label: loc.name, value: loc.name })));

const filterBarConfig = computed(() => ({
    route: route('reports.incident-log'),
    defaultSort: { column: 'start_date', asc: false },
    routeOptions: { only: ['incidents'] },
    filters: [
        { type: 'date-range', label: trans('activities.overview.cell.date'), startKey: 'start', endKey: 'end' },
        {
            type: 'multi-select',
            key: 'types',
            label: trans('assets.overview.filter.type'),
            options: props.filters.types?.data ?? [],
        },
        {
            type: 'multi-select',
            key: 'specificLocations',
            label: trans('activities.overview.cell.specific_location'),
            options: specificLocations.value,
            showSearchbar: true,
        },
        {
            type: 'multi-select',
            key: 'areas',
            label: trans('locations.area.label'),
            options: areas.value,
            showSearchbar: true,
        },
    ],
}));

const fields = [
    { key: 'start_date', label: 'Start Date', sorter: false },
    { key: 'end_date', label: 'End Date', sorter: false },
    { key: 'duration', label: 'Duration', sorter: false },
    { key: 'type', label: 'Type', sorter: false },
    { key: 'designation', label: 'Designation', sorter: false },
    { key: 'curie_id', label: 'Curie ID', sorter: false },
    { key: 'manufacturer', label: 'Vendor', sorter: false },
    { key: 'specific_location', label: 'Specific Location', sorter: false },
    { key: 'area', label: 'Area', sorter: false },
    { key: 'report', label: 'Report', sorter: false },
    { key: 'reporter', label: 'Reporter', sorter: false },
    { key: 'tickets', label: 'Related Tickets', sorter: false },
];

function openAssetDetails(assetId) {
    router.visit(route('assets.show', assetId));
}

function showWorkorder(id) {
    visitModal(route('workorders.show', id));
}
</script>
